import { SmartNotificationsSettingsResource } from '~/resources';
import { BADGE_CHECK } from '~/rails_assets';
import { SETTINGS, SETTINGS_SMART_NOTIFICATIONS, GETTING_STARTED } from '~/app_constants';

export default {
  props: {
    notifications: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every((notification) => {
          const notificationKeys = Object.keys(notification);

          return ['id', 'key'].every((key) => notificationKeys.includes(key));
        });
      },
    },
    'insights-trial-started': {
      type: Boolean,
      default: false,
    },
    'insights-trial-monthly-price': {
      type: Number,
      required: true,
      validator: (value) => {
        return value > 0;
      },
    },
    'substep-status': {
      type: String,
      default: 'current',
    },
    source: {
      type: String,
      validator: (value) => {
        return ['getting_started_flow', 'insights_onboarding_flow'].includes(value);
      },
    },
    organization: {
      type: Object,
      required: true,
      validator: (value) => {
        const valueKeys = Object.keys(value);

        return valueKeys.includes('id');
      },
    },
  },
  data() {
    return {
      assets: SETTINGS.smart_notifications,
      images: GETTING_STARTED,
      dialogDismissed: false,
      BADGE_CHECK,
      submitting: [],
      defaultAttributes: {
        target: {
          members: ['all'],
        },
        recipients: {
          roles: ['owner', 'manager'],
        },
      },
    };
  },
  computed: {
    showDialog() {
      return this.insightsTrialStarted && !this.dialogDismissed;
    },
    notificationTemplates() {
      if (!this.assets?.presets) {
        return {};
      }

      return Object.entries(this.assets.presets)
        .map(([key, value]) => {
          const template = { key, ...value };

          if (template.label === SETTINGS_SMART_NOTIFICATIONS.presets.high_activity.label) {
            template.description = template.description.replace('90%', '95%');
          }

          if (template.label === SETTINGS_SMART_NOTIFICATIONS.presets.low_activity.label) {
            template.frequency = 'Daily';
            template.description = template.description.replace('35%', '20%');
          }

          return template;
        })
        .filter((template) => this.notificationPresets.includes(template.key))
        .reduce((acc, template, index) => {
          acc[index] = template;

          return acc;
        }, {});
    },
  },
  watch: {
    notifications: {
      handler() {
        this.checkNotifications();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    notificationExists(label) {
      return this.notifications.some((notification) => notification.name === label);
    },
    checkNotifications() {
      const hasEnabledNotification = this.notifications.length > 0;

      if (hasEnabledNotification && this.substepStatus !== 'completed') {
        this.$emit('completeSubstep');
      }

      if (!hasEnabledNotification && this.substepStatus === 'completed') {
        this.$emit('incompleteSubstep');
      }
    },
    async createNotification(notification, index) {
      try {
        this.submitting[index] = true;
        const params = {
          ...this.parseNotificationParams(notification),
          source: this.source,
        };
        const response = await SmartNotificationsSettingsResource.create(
          { organizationId: this.organization.id },
          params,
        );

        if (response.success) {
          this.$emit('reloadSubstep');
        } else if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      } finally {
        this.submitting[index] = false;
      }
    },
    async deleteNotification(notification, index) {
      try {
        this.submitting[index] = true;
        const notificationId = this.notifications.find((n) => n.name === notification.label).id;

        if (!notificationId) {
          this.$emit('reloadSubstep');
        }

        const response = await SmartNotificationsSettingsResource.delete({
          organizationId: this.organization.id,
          smartNotificationId: notificationId,
        });

        if (response.success) {
          this.$emit('reloadSubstep');
        } else if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      } finally {
        this.submitting[index] = false;
      }
    },
    parseNotificationParams(notification) {
      const presetAttributes = SETTINGS_SMART_NOTIFICATIONS.presets[notification.key];
      const updatedPresetAttributes = this.updatedPresetAttributes(presetAttributes);

      return {
        ...SETTINGS_SMART_NOTIFICATIONS.defaultFormSetting,
        ...this.defaultAttributes,
        ...updatedPresetAttributes,
      };
    },
    closeDialog() {
      this.dialogDismissed = true;
    },
    updatedPresetAttributes(presetAttributes) {
      const deliverByArray = Object.entries(presetAttributes.deliver_by)
        .filter(([key, value]) => value)
        .map(([key]) => key);
      const updatedPresetAttributes = { ...presetAttributes, deliver_by: deliverByArray };

      if (updatedPresetAttributes.name === SETTINGS_SMART_NOTIFICATIONS.presets.high_activity.label) {
        updatedPresetAttributes.condition_value = 95;
        updatedPresetAttributes.recipients = {roles: ['owner', 'manager', 'team_lead'], members: []}
      }

      if (updatedPresetAttributes.name === SETTINGS_SMART_NOTIFICATIONS.presets.low_activity.label) {
        updatedPresetAttributes.condition_value = 20;
        updatedPresetAttributes.frequency = 'daily';
        updatedPresetAttributes.recipients = {roles: ['owner', 'manager', 'team_lead'], members: []}
      }

      if (updatedPresetAttributes.metric_type === 'work_time') {
        const [hours, minutes] = updatedPresetAttributes.condition_value.split(':');

        updatedPresetAttributes.condition_value = Number(hours) * 3600 + Number(minutes) * 60;
      }

      if (['time_spent_in_app', 'time_spent_in_tool'].includes(updatedPresetAttributes.metric_type)) {
        updatedPresetAttributes.tool_type = updatedPresetAttributes.metric_type === 'time_spent_in_app' ? 'application' : 'url';
        updatedPresetAttributes.metric_type = 'time_spent_in_tool';
        updatedPresetAttributes.tool_names = updatedPresetAttributes.tools.names;
      }

      if (!SETTINGS_SMART_NOTIFICATIONS.options.timeBasedMetrics.includes(presetAttributes.metric_type)) {
        updatedPresetAttributes.time_unit = null;
      }

      return updatedPresetAttributes;
    },
  },
};
